import { FC, useMemo } from 'react'
import * as styles from 'styles/molecules/forms/Form.css'
import * as yup from 'yup'
import { Field, reduxForm, formValueSelector } from 'redux-form'
import { FORM_MEMBER_PROFILE } from 'constants/forms'
import { ReduxFormProps } from 'interfaces/component'
import { profileEditSchema } from 'schemas/profileEditSchema'
import { getSaveButtonFormProps, shouldAsyncValidate } from 'helpers/formHelpers'
import { FIELD_HOUSING_TYPE_PROPS, FIELD_INCOME_PROPS } from 'components/features/pfm/PropertyForm/PropertyForm'
import { asyncValidator } from 'helpers/schemaHelpers'
import TextFieldWrapped from 'components/atoms/inputs/TextFieldWrapped'
import { ButtonStyle } from 'components/features/pfm/Button/Button'
import CityStateField from 'components/atoms/inputs/CityStateField'
import ButtonRow from 'components/atoms/buttons/ButtonRow'
import Anchor from 'components/atoms/navigation/Anchor'
import SelectWrapped from 'components/atoms/inputs/OldSelectWrapped'
import { FIELD_ADDRESS1_PROPS, FIELD_ADDRESS2_PROPS, FIELD_CURRENCY_WRAPPED_PROPS, FIELD_ZIP_PROPS, FIELD_USERNAME_PROPS } from 'constants/fields'
import { AvatarField } from 'components/features/socialFeed/AvatarField/AvatarField'
import { useStoreSelector } from 'helpers/reactReduxHelpers'
import { Cropper } from 'components/features/pfm/Cropper/Cropper'
import { cropperInputSelector } from 'selectors/cropperSelectors'
import { defaultMemoize } from 'reselect'
import { TYPE_REQUIRED } from 'constants/errors'

export type MemberProfileProps = ReduxFormProps & {
  onCancel: () => void
}

const MemberProfileForm: FC<MemberProfileProps> = (props) => {
  const { handleSubmit, onCancel } = props

  const fieldValueSelector = useMemo(() => formValueSelector(FORM_MEMBER_PROFILE), [])
  const username = useStoreSelector<string | null>((state) => fieldValueSelector(state, 'username'))
  const cropperImage = useStoreSelector(cropperInputSelector)

  return (
    <form className={ styles.form } onSubmit={ handleSubmit }>
      <ul>
        <li className={ styles.item }>
          { !cropperImage && (
            <div className={ styles.avatar }>
              <Anchor placement='settings_income'>
                <Field
                  name='avatar'
                  component={ AvatarField }
                  props={{
                    username,
                    inFormMode: true,
                  }}
                />
              </Anchor>
            </div>
          )}
          <Cropper theme='settings' />
        </li>
        <li className={ styles.item }>
          <Field
            name='username'
            props={{ ...FIELD_USERNAME_PROPS, autoFocus: true }}
            component={ TextFieldWrapped }
          />
        </li>
        <li className={ styles.item }>
          <Field
            name='income'
            props={ FIELD_INCOME_PROPS }
            component={ TextFieldWrapped }
            { ...FIELD_CURRENCY_WRAPPED_PROPS }
          />
        </li>
        <li className={ styles.item }>
          <Field
            name='housing_type'
            props={{ ...FIELD_HOUSING_TYPE_PROPS, theme: 'white' }}
            component={ SelectWrapped }
          />
        </li>
        <li className={ styles.item }>
          <Field
            name='address1'
            component={ TextFieldWrapped }
            props={ FIELD_ADDRESS1_PROPS }
          />
        </li>
        <li className={ styles.item }>
          <Field
            name='address2'
            component={ TextFieldWrapped }
            props={ FIELD_ADDRESS2_PROPS }
          />
        </li>
        <li className={ styles.item }>
          <CityStateField name='city' />
        </li>
        <li className={ styles.item }>
          <Field
            name='zip5'
            props={ FIELD_ZIP_PROPS }
            component={ TextFieldWrapped }
          />
        </li>
        <li className={ styles.item }>
          <ButtonRow
            size='small'
            buttons={ [
              { text: 'Save', submit: true, ...getSaveButtonFormProps(props), qaMarker: 'submit-button' },
              { text: 'Cancel', style: 'hollow' as ButtonStyle, onClick: onCancel, qaMarker: 'cancel-button' },
            ] }
          />
        </li>
      </ul>
    </form>
  )
}

const getSchema = defaultMemoize((hadUsername: boolean) => {
  const adjustSchema = hadUsername
    ? (schema: any) => schema.required(TYPE_REQUIRED)
    : (schema: any) => schema.notRequired()

  return profileEditSchema.shape({
    username: adjustSchema(yup.reach(profileEditSchema, 'username')),
  })
})

export default reduxForm({
  form: FORM_MEMBER_PROFILE,
  asyncValidate: (values, _dispatch, props: ReduxFormProps) => {
    const { initialValues } = props
    const schema = getSchema(Boolean(initialValues && initialValues.username))

    return asyncValidator(schema)(values)
  },
  shouldAsyncValidate,
})(MemberProfileForm)
